/**

 * Shared product specification type helpers.

 * Single place to normalize API values and check spec type (e.g. Produce RAC variants).

 */



import { YES_NO_OPTIONS } from "@/constants/yesNo";



/** Status bar `label_value` keys omitted for All Branded products. */

export const ALL_BRANDED_COLLAPSED_WORKFLOW_STEP_KEYS: readonly string[] = [

  "UNDER_LEGAL_REVIEW",

  "ARTWORK_IN_DESIGN",

];

export const PRODUCE_RAC_BRANDED_COLLAPSED_WORKFLOW_STEP_KEYS: readonly string[] = [

  "UNDER_LEGAL_REVIEW",

  "ARTWORK_IN_DESIGN",

];

/** Status bar `label_value` keys omitted for Produce RAC Branded + Opportunity Buy = Yes. */
export const OPPORTUNITY_BUY_YES_COLLAPSED_WORKFLOW_STEP_KEYS: readonly string[] = [
  "UNDER_PRODUCT_DEVELOPER_REVIEW",
  "UNDER_LEGAL_REVIEW",
  "ARTWORK_IN_DESIGN",
  "FINAL_SPECIFICATION_REVIEW_AND_ACKNOWLEDGEMENT_VENDOR",
  "FINAL_SPECIFICATION_REVIEW_AND_APPROVAL_TJ",
];



/** Known API / dropdown labels for product specification types. */

export const PRODUCT_SPECIFICATION_TYPES = {

  PRODUCE_RAC_TJ_LABEL: "Produce RAC - Trader Joe’s Label",

  PRODUCE_RAC_BRANDED: "Produce RAC - Branded",

} as const;



/**

 * Trim and decode common API/label formatting (display-safe string).

 */

export function normalizeProductSpecificationTypeLabel(

  value: string | null | undefined

): string {

  return String(value ?? "")

    .replace(/&amp;/g, "&")

    .trim();

}



/**

 * Normalize for equality / includes checks (case, dashes, whitespace).

 */

export function normalizeProductSpecificationTypeForComparison(

  value: string | null | undefined

): string {

  return normalizeProductSpecificationTypeLabel(value)

    .replace(/[\u2013\u2014-]/g, "-")

    .toLowerCase()

    .replace(/\s+/g, " ")

    .trim();

}



function normalizedSpecType(spec?: string | null): string {
  return normalizeProductSpecificationTypeForComparison(spec);
}

/** Normalize API productSpecificationType string (trim, decode &amp;). */
export function resolveProductSpecificationType(
  raw?: string | null
): string {
  if (raw == null || typeof raw !== "string") return "";
  return normalizeProductSpecificationTypeLabel(raw);
}

/** First non-empty spec type from one or more API string sources. */
export function resolveProductSpecificationTypeFromApiSources(
  ...sources: (string | null | undefined)[]
): string {

  for (const source of sources) {

    const resolved = resolveProductSpecificationType(source);

    if (resolved) return resolved;

  }

  return "";

}

/**
 * Reads productSpecificationType from vendor GET `productIdeas[].idea`.
 * When ideaIdHint is set, matches `idea.id`; otherwise uses the sole entry when length is 1.
 */
export function resolveProductSpecificationTypeFromProductIdeas(
  productIdeas: unknown,
  ideaIdHint?: string | number | null
): string {
  if (!Array.isArray(productIdeas) || productIdeas.length === 0) {
    return "";
  }

  const hint =
    ideaIdHint != null && String(ideaIdHint).trim() !== ""
      ? String(ideaIdHint).trim()
      : "";

  let matchedIdea: Record<string, unknown> | undefined;

  if (hint !== "") {
    for (const item of productIdeas) {
      if (item == null || typeof item !== "object") continue;
      const idea = (item as Record<string, unknown>).idea;
      if (idea == null || typeof idea !== "object" || Array.isArray(idea)) {
        continue;
      }
      const ideaRecord = idea as Record<string, unknown>;
      if (String(ideaRecord.id ?? "").trim() === hint) {
        matchedIdea = ideaRecord;
        break;
      }
    }
  }

  if (!matchedIdea && productIdeas.length === 1) {
    const item = productIdeas[0];
    if (item != null && typeof item === "object") {
      const idea = (item as Record<string, unknown>).idea;
      if (idea != null && typeof idea === "object" && !Array.isArray(idea)) {
        matchedIdea = idea as Record<string, unknown>;
      }
    }
  }

  if (!matchedIdea) {
    return "";
  }

  const specType = matchedIdea.productSpecificationType;
  return typeof specType === "string"
    ? resolveProductSpecificationType(specType)
    : "";
}

/** Spec type label from a product idea GET response (`productSpecificationType` object or string). */
export function resolveProductSpecificationTypeFromIdeaResponse(
  idea: unknown
): string {
  if (idea == null || typeof idea !== "object" || Array.isArray(idea)) {
    return "";
  }

  const pst = (idea as Record<string, unknown>).productSpecificationType;
  if (pst == null) return "";
  if (typeof pst === "string") {
    return resolveProductSpecificationType(pst);
  }
  if (typeof pst === "object" && !Array.isArray(pst)) {
    const record = pst as Record<string, unknown>;
    return resolveProductSpecificationType(
      String(record.attributeLabel ?? record.value ?? record.id ?? "")
    );
  }

  return "";
}

/** Spec type from a vendor assessment GET row (`productIdeas` on guest/TJ getVendor response). */
export function resolveProductSpecificationTypeFromVendorGetResponse(
  data: unknown,
  ideaIdHint?: string | number | null
): string {
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return "";
  }
  return resolveProductSpecificationTypeFromProductIdeas(
    (data as Record<string, unknown>).productIdeas,
    ideaIdHint
  );
}

function matchesProductSpecificationType(
  spec: string | null | undefined,
  canonicalLabel: string
): boolean {
  const normalized = normalizedSpecType(spec);
  if (!normalized) return false;
  return (
    normalized === normalizeProductSpecificationTypeForComparison(canonicalLabel)
  );
}

/** True when product spec is Produce RAC - Trader Joe's Label (API canonical string). */

export function isProduceRacTjLabelProductSpecificationType(

  spec?: string | null

): boolean {

  return matchesProductSpecificationType(
    spec,
    PRODUCT_SPECIFICATION_TYPES.PRODUCE_RAC_TJ_LABEL
  );

}



/** True when product spec is Produce RAC - Branded (API canonical string). */

export function isProduceRacBrandedProductSpecificationType(

  spec?: string | null

): boolean {

  if (isProduceRacTjLabelProductSpecificationType(spec)) return false;

  if (isAllBrandedProductSpecificationType(spec)) return false;

  return matchesProductSpecificationType(
    spec,
    PRODUCT_SPECIFICATION_TYPES.PRODUCE_RAC_BRANDED
  );

}



/** True when vendor assessment uses a Produce RAC field layout (TJ Label or Branded). */

export function isProduceRacVendorAssessmentLayout(

  spec?: string | null

): boolean {

  return (
    isProduceRacTjLabelProductSpecificationType(spec) ||
    isProduceRacBrandedProductSpecificationType(spec)
  );

}



/**
 * True when the Review Workflow popup should show on vendor assessment review complete:
 * - Produce RAC TJ Label (unchanged), or
 * - Produce RAC Branded with Opportunity Buy = No.
 */
export function shouldShowReviewWorkflowPopupForSpecification(
  spec?: string | null,
  opportunityBuy?: string | null
): boolean {
  if (isProduceRacTjLabelProductSpecificationType(spec)) {
    return true;
  }

  const isOpportunityBuyNo =
    String(opportunityBuy ?? "").trim() === (YES_NO_OPTIONS[1]?.value ?? "No");

  return isProduceRacBrandedProductSpecificationType(spec) && isOpportunityBuyNo;
}



/**

 * True when the product is All Branded (aligned with roleBasedFieldPermissions patterns).

 */

export function isAllBrandedProductSpecificationType(

  spec?: string | null

): boolean {

  const normalized = normalizedSpecType(spec);

  if (!normalized) return false;

  return (

    normalized.includes("all branded") || normalized.includes("branded products")

  );

}



/**

 * Returns keys to hide from the stepper based on product specification type and Opportunity Buy.
 * - All Branded: legal review + artwork steps.
 * - Produce RAC Branded with Opportunity Buy = Yes: Under Product Developer Review and steps after Under Internal Review through final spec approval.
 */

export function getHideWorkflowStepKeysForSpecificationType(

  productSpecificationType?: string | null,

  opportunityBuy?: string | null,

): string[] | undefined {

  const hidden = new Set<string>();
  const { isProduceRacBranded } = getProductSpecificationFlags(productSpecificationType ?? "");
  if (isAllBrandedProductSpecificationType(productSpecificationType)) {
    ALL_BRANDED_COLLAPSED_WORKFLOW_STEP_KEYS.forEach((key) => hidden.add(key));
  }

const normalizedOpportunityBuy = String(opportunityBuy ?? "").trim().toLowerCase();

const isOpportunityBuyYes = isProduceRacBranded && normalizedOpportunityBuy === "yes";

const isOpportunityBuyNo = isProduceRacBranded && normalizedOpportunityBuy === "no";

  if (isOpportunityBuyYes) {
    OPPORTUNITY_BUY_YES_COLLAPSED_WORKFLOW_STEP_KEYS.forEach((key) => hidden.add(key));
  }

  if (isOpportunityBuyNo) {
    PRODUCE_RAC_BRANDED_COLLAPSED_WORKFLOW_STEP_KEYS.forEach((key) => hidden.add(key));
  }

  return hidden.size > 0 ? [...hidden] : undefined;

}



/** Removes skip flags for steps that are not rendered. */

export function filterSkippedStepKeysForVisibleSteps(

  skippedStepKeys: string[],

  hideWorkflowStepKeys: string[] | undefined

): string[] {

  if (!hideWorkflowStepKeys?.length) return skippedStepKeys;

  const hidden = new Set(hideWorkflowStepKeys);

  return skippedStepKeys.filter((key) => !hidden.has(key));

}



export type WorkflowStepWithCanonical<

  T extends { label_value?: string; stepNo: number },

> = T & {

  canonicalStepNo?: number;

};



/**

 * When `hideKeys` is set: drop those steps, set `canonicalStepNo` from config `stepNo`,

 * renumber display `stepNo` to 1..N. When unset: shallow copies of `steps`.

 */

export function applyWorkflowStepCollapsing<

  T extends { label_value?: string; stepNo: number },

>(steps: T[], hideKeys: string[] | undefined): WorkflowStepWithCanonical<T>[] {

  if (!hideKeys?.length) {

    return steps.map((s) => ({ ...s }));

  }

  const hide = new Set(hideKeys);

  const filtered = steps.filter(

    (s) => s.label_value && !hide.has(s.label_value)

  );

  return filtered.map((s, i) => {

    const canonicalStepNo = Number(s.stepNo) || 0;

    return { ...s, canonicalStepNo, stepNo: i + 1 };

  });

}

export type ProductSpecificationTypeListItem = {
  attributeLabel?: string;
  attributeValue?: string;
  id?: string | number;
};

/** Map stored dropdown value (label, value, or id) to candidate strings for comparison. */
export const resolveProductSpecificationTypeCandidates = (
  storedValue?: string | null,
  specificationTypeList?: ProductSpecificationTypeListItem[]
): string[] => {
  if (!storedValue?.trim()) return [];

  const list = specificationTypeList ?? [];
  const match = list.find(
    item =>
      String(item.attributeValue ?? "") === storedValue ||
      String(item.attributeLabel ?? "") === storedValue ||
      String(item.id ?? "") === storedValue
  );

  return [storedValue, match?.attributeLabel, match?.attributeValue].filter(
    (value): value is string => Boolean(value?.trim())
  );
};

/** Ideation/forms: stored value may be attributeValue/id; resolve then exact-match API label. */
export const isProduceRacBrandedSpecificationType = (
  storedValue?: string | null,
  specificationTypeList?: ProductSpecificationTypeListItem[]
): boolean =>
  resolveProductSpecificationTypeCandidates(storedValue, specificationTypeList).some(
    candidate => isProduceRacBrandedProductSpecificationType(candidate)
  );

export const isProduceRacTjLabelSpecificationType = (
  storedValue?: string | null,
  specificationTypeList?: ProductSpecificationTypeListItem[]
): boolean =>
  resolveProductSpecificationTypeCandidates(storedValue, specificationTypeList).some(
    candidate => isProduceRacTjLabelProductSpecificationType(candidate)
  );


export enum ProductSpecificationType {
  FOOD_AND_BEVERAGES = "Food & Beverages",
  ALL_BRANDED_PRODUCTS = "All Branded Products",
  HABA = "HABA",
  PET_FOODS = "Pet Foods",
  PRODUCE_RAC_TRADERS_JOE_LABEL = "Produce RAC - Trader Joe’s Label",
  PRODUCE_RAC_BRANDED = "Produce RAC - Branded",
}

interface ProductSpecificationFlags {
  normalizedSpecType: string;
  isFoodAndBeverages: boolean;
  isAllBranded: boolean;
  isHaba: boolean;
  isPetFoods: boolean;
  isProduceRacTradersJoeLabel: boolean;
  isProduceRacBranded: boolean;
}

export const getProductSpecificationFlags = (
  productSpecificationType?: string
): ProductSpecificationFlags => {
  const normalizedSpecType = (productSpecificationType ?? "")
    .replace(/&amp;/g, "&")
    .replace("HABA (Health and Beauty Aids)", ProductSpecificationType.HABA)
    .trim();

  return {
    normalizedSpecType,

    isFoodAndBeverages: [
      ProductSpecificationType.FOOD_AND_BEVERAGES,
    ].includes(normalizedSpecType as ProductSpecificationType),

    isAllBranded:
      normalizedSpecType === ProductSpecificationType.ALL_BRANDED_PRODUCTS,

    isHaba:
      normalizedSpecType === ProductSpecificationType.HABA,

    isPetFoods:
      normalizedSpecType === ProductSpecificationType.PET_FOODS,

    isProduceRacTradersJoeLabel:
      normalizedSpecType ===
      ProductSpecificationType.PRODUCE_RAC_TRADERS_JOE_LABEL,

    isProduceRacBranded:
      normalizedSpecType ===
      ProductSpecificationType.PRODUCE_RAC_BRANDED,
  };
};